import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbRefresh, TbCheck, TbCopy } from "react-icons/tb";
import { Section, SettingRow } from "src/pages/Settings/SettingsControls";
import { useAuth } from "src/context/AuthProvider";
import { isDoctor, isAdmin } from "src/utils/roles";
import Popup from "src/components/Popup/Popup";
import { RecencyChip } from "src/components/RecencyChip/RecencyChip";
import {
    listDevices, createEnrollmentToken, createRenewalToken, revokeDevice, deleteDevice, setDeviceSharedFlag, type DeviceDTO,
} from "src/services/deviceService";
import { AgentDownloadRow } from "./AgentDownloadRow";
import "./deviceManagementSection.scss";

// One shared shape for both "generate enrollment code" and "renew" — same one-time-token
// reveal UI either way (backend/src/controllers/deviceController.ts's enrollDevice treats them
// as the same mechanism, distinguished only by whether the token is tied to an existing
// device). deviceId is set only for a renewal, purely for the confirmation copy ("renewal code
// for <label>") — it plays no role in the exchange itself.
interface RevealedToken {
    token: string;
    expiresAt: string;
    deviceId?: string;
}

/** "MEDICAL OFFICE 2025.2 · Desktop-App 1.0.4 · GDT 02.10" under the device name — what support asks
 *  first. Nothing when the app reported none of it. */
function DeviceDetails({ device: d }: { device: DeviceDTO }) {
    const { t } = useTranslation();
    const details = [
        [d.practiceSoftware, d.practiceSoftwareVersion].filter(Boolean).join(" "),
        d.clientVersion && t("devices.detailsApp", { version: d.clientVersion }),
        d.gdtSource?.gdtVersion && t("devices.detailsGdt", { version: d.gdtSource.gdtVersion }),
    ].filter(Boolean).join(" · ");
    return details ? <span className="device-mgmt__details">{details}</span> : null;
}

const STATUS_TONE: Record<DeviceDTO["status"], string> = {
    active: "green",
    expiring_soon: "orange",
    expired: "red",
    revoked: "grey",
};

/** Device management for the desktop agent, at the top of the Sync page. Enrolment codes are
 *  one-time and single-use; revoking is immediate (info/Sync/praxis-software-sync-agent-runbook.md). */
function DeviceManagementPanel() {
    const { t } = useTranslation();
    const [devices, setDevices] = useState<DeviceDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [generating, setGenerating] = useState(false);
    const [renewingId, setRenewingId] = useState<string | null>(null);
    const [revokingId, setRevokingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [confirmRevoke, setConfirmRevoke] = useState<DeviceDTO | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<DeviceDTO | null>(null);
    const [togglingSharedId, setTogglingSharedId] = useState<string | null>(null);
    const [revealed, setRevealed] = useState<RevealedToken | null>(null);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await listDevices();
            setDevices(res);
        } catch {
            setError(t("devices.loadError"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { void load(); }, [load]);

    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const res = await createEnrollmentToken();
            setRevealed({ token: res.token, expiresAt: res.expiresAt });
            setCopied(false);
        } catch {
            setError(t("devices.generateError"));
        } finally {
            setGenerating(false);
        }
    };

    const handleRenew = async (device: DeviceDTO) => {
        setRenewingId(device.id);
        setError(null);
        try {
            const res = await createRenewalToken(device.id);
            setRevealed({ token: res.token, expiresAt: res.expiresAt, deviceId: device.id });
            setCopied(false);
        } catch {
            setError(t("devices.renewError"));
        } finally {
            setRenewingId(null);
        }
    };

    const handleRevoke = async () => {
        if (!confirmRevoke) return;
        setRevokingId(confirmRevoke.id);
        setError(null);
        try {
            await revokeDevice(confirmRevoke.id);
            setConfirmRevoke(null);
            await load();
        } catch {
            setError(t("devices.revokeError"));
        } finally {
            setRevokingId(null);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeletingId(confirmDelete.id);
        setError(null);
        try {
            await deleteDevice(confirmDelete.id);
            setConfirmDelete(null);
            await load();
        } catch {
            setError(t("devices.deleteError"));
        } finally {
            setDeletingId(null);
        }
    };

    const handleToggleShared = async (device: DeviceDTO) => {
        setTogglingSharedId(device.id);
        setError(null);
        const next = !device.sharedDevice;
        // Optimistic — this is a low-stakes toggle staff should see react immediately;
        // load() below reconciles with the server either way.
        setDevices((prev) => prev.map((d) => (d.id === device.id ? { ...d, sharedDevice: next } : d)));
        try {
            await setDeviceSharedFlag(device.id, next);
        } catch {
            setError(t("devices.sharedToggleError"));
            setDevices((prev) => prev.map((d) => (d.id === device.id ? { ...d, sharedDevice: device.sharedDevice } : d)));
        } finally {
            setTogglingSharedId(null);
        }
    };

    const copyToken = async () => {
        if (!revealed) return;
        try {
            await navigator.clipboard.writeText(revealed.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError(t("devices.copyError"));
        }
    };

    return (
        <Section title={t("devices.title")} description={t("devices.description")}>
            <AgentDownloadRow />
            <SettingRow title={t("devices.rowTitle")} description={t("devices.rowDesc")} column>
                <div className="device-mgmt">
                    <div className="device-mgmt__toolbar">
                        <button type="button" className="btn-outline" onClick={() => void handleGenerate()} disabled={generating}>
                            {generating ? t("devices.generating") : t("devices.generateBtn")}
                        </button>
                        <button type="button" className="device-mgmt__refresh" onClick={() => void load()} disabled={loading} aria-label={t("devices.refreshBtn")}>
                            <TbRefresh />
                        </button>
                    </div>

                    {revealed && (
                        <div className="device-mgmt__reveal">
                            <p className="device-mgmt__reveal-label">
                                {revealed.deviceId ? t("devices.renewalCodeLabel") : t("devices.enrollmentCodeLabel")}
                            </p>
                            <div className="device-mgmt__reveal-row">
                                <code className="device-mgmt__reveal-token">{revealed.token}</code>
                                <button type="button" className="btn-outline" onClick={() => void copyToken()}>
                                    {copied ? <><TbCheck /> {t("devices.copied")}</> : <><TbCopy /> {t("devices.copyBtn")}</>}
                                </button>
                            </div>
                            <p className="device-mgmt__reveal-expiry">
                                {t("devices.codeExpires", { time: new Date(revealed.expiresAt).toLocaleString("de-DE") })}
                            </p>
                        </div>
                    )}

                    {error && <p className="device-mgmt__error">{error}</p>}

                    {loading ? (
                        <span className="device-mgmt__state">{t("common.loading")}</span>
                    ) : devices.length === 0 ? (
                        <span className="device-mgmt__state">{t("devices.emptyState")}</span>
                    ) : (
                        <div className="device-mgmt__table-wrap">
                            <table className="device-mgmt__table">
                                <thead>
                                    <tr>
                                        <th>{t("devices.colLabel")}</th>
                                        <th>{t("devices.colLastSeen")}</th>
                                        <th>{t("devices.colStatus")}</th>
                                        <th>{t("devices.colExpires")}</th>
                                        <th>{t("devices.colShared")}</th>
                                        <th>{t("devices.colActions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {devices.map((d) => (
                                        <tr key={d.id}>
                                            <td>
                                                <span className="device-mgmt__label">{d.label}</span>
                                                <DeviceDetails device={d} />
                                            </td>
                                            <td>
                                                {d.lastSeenAt
                                                    ? <RecencyChip timestamp={d.lastSeenAt} />
                                                    : <span className="device-mgmt__never">{t("devices.neverSeen")}</span>}
                                            </td>
                                            <td>
                                                {d.status === "revoked" || d.status === "expired" ? (
                                                    <span className={`device-mgmt__status device-mgmt__status--${STATUS_TONE[d.status]}`}>
                                                        {t(`devices.status.${d.status}`)}
                                                    </span>
                                                ) : (
                                                    // A valid credential says nothing about whether the agent is running, so lead with
                                                    // that (heard from in the last 15 min) and add the credential warning beside it.
                                                    <span className="device-mgmt__status-group">
                                                        <span className={`device-mgmt__status device-mgmt__status--${d.connected ? "green" : "grey"}`}>
                                                            {d.connected ? t("devices.connection.connected") : t("devices.connection.notConnected")}
                                                        </span>
                                                        {d.status === "expiring_soon" && (
                                                            <span className="device-mgmt__status device-mgmt__status--orange">{t("devices.status.expiring_soon")}</span>
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="device-mgmt__expires">{new Date(d.expiresAt).toLocaleDateString("de-DE")}</td>
                                            <td>
                                                <label className="device-mgmt__shared-toggle" title={t("devices.sharedHint")}>
                                                    <input
                                                        type="checkbox"
                                                        checked={d.sharedDevice}
                                                        disabled={togglingSharedId === d.id}
                                                        onChange={() => void handleToggleShared(d)}
                                                    />
                                                    <span>{t("devices.sharedLabel")}</span>
                                                </label>
                                            </td>
                                            <td>
                                                <div className="device-mgmt__actions">
                                                    {/* Currently connected = a live, in-use device — Renew/Revoke, handled
                                                        carefully. Anything else (revoked, expired, or simply not heard from
                                                        right now, regardless of whether it ever connected) is not doing
                                                        anything at this moment, so it's safe for staff to just remove if they
                                                        don't want it cluttering the list — Delete, not Renew/Revoke. Re-enrolling
                                                        the same physical machine only works if its agent still remembers its
                                                        own device id locally; any time that's lost (reinstalled OS, wiped
                                                        profile, ...) the old row is stranded with no way back to it anyway. */}
                                                    {d.connected ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn-outline"
                                                                onClick={() => void handleRenew(d)}
                                                                disabled={renewingId === d.id}
                                                            >
                                                                {renewingId === d.id ? t("devices.renewing") : t("devices.renewBtn")}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="device-mgmt__revoke-btn"
                                                                onClick={() => setConfirmRevoke(d)}
                                                            >
                                                                {t("devices.revokeBtn")}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="device-mgmt__revoke-btn"
                                                            onClick={() => setConfirmDelete(d)}
                                                        >
                                                            {t("devices.deleteBtn")}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </SettingRow>

            {confirmRevoke && (
                <Popup
                    title={t("devices.revokeBtn")}
                    message={t("devices.revokeConfirm", { label: confirmRevoke.label })}
                    onClose={() => setConfirmRevoke(null)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", disabled: revokingId === confirmRevoke.id, onClick: () => setConfirmRevoke(null) },
                        { label: t("devices.revokeBtn"), variant: "danger", disabled: revokingId === confirmRevoke.id, onClick: () => void handleRevoke() },
                    ]}
                />
            )}

            {confirmDelete && (
                <Popup
                    title={t("devices.deleteBtn")}
                    message={t("devices.deleteConfirm", { label: confirmDelete.label })}
                    onClose={() => setConfirmDelete(null)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", disabled: deletingId === confirmDelete.id, onClick: () => setConfirmDelete(null) },
                        { label: t("devices.deleteBtn"), variant: "danger", disabled: deletingId === confirmDelete.id, onClick: () => void handleDelete() },
                    ]}
                />
            )}
        </Section>
    );
}

/** Gated exactly like the backend's requireSettings (role default Doctor/Admin, else an explicit
 *  permSettings === true): enrolling a device mints real API access for an unattended process, so
 *  anyone who would only get a 403 on every action simply doesn't see it. The rest of the Sync page
 *  stays visible to everyone who can open it. */
export function useCanManageDevices(): boolean {
    const { user } = useAuth();
    const userRole = user?.doctor?.userRights ?? 0;
    return isDoctor(userRole) || isAdmin(userRole) || user?.doctor?.permSettings === true;
}

export function DeviceManagementSection() {
    return useCanManageDevices() ? <DeviceManagementPanel /> : null;
}
